import { Sidebar } from "primereact/sidebar";
import "./ranking-detail.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useQyGetAbsentRanking,
  useQyGetAmendedRanking,
  useQyGetLateRanking,
  useQyGetOnLeaveRanking,
  useQyGetOvertimeRanking,
  useQyGetUndertimeRanking,
} from "@core/query/report.query";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { useState } from "react";
import { TableSort } from "@core/model/table-sort";
import { numberFormat } from "@shared/utility/formats/number-format";
import { DashboardRanking } from "@core/model/dashboard-ranking.enum";
import {
  EmployeePerformanceModel,
  GetLateRankingResponse,
} from "@core/model/query-model-report";

/* eslint-disable-next-line */
export interface LateRankingProps {}

export function RankingDetail() {
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});

  const navigate = useNavigate();
  const [response, setResponse] = useState<GetLateRankingResponse | null>(null);

  let [searchParams] = useSearchParams();
  const type = searchParams.get("type") ?? "";
  const startDate = searchParams.get("startDate") ?? "";
  const endDate = searchParams.get("endDate") ?? "";
  const branchId = searchParams.get("branchId") ?? undefined;
  const tallyDays = [
    DashboardRanking.Absent,
    DashboardRanking.Amended,
    DashboardRanking.OnLeave,
  ];

  // QUERY
  const getLateRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetLateRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.Late,
    getLateRankingApi
  );

  const getOvertimeRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetOvertimeRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.Overtime,
    getOvertimeRankingApi
  );

  const getAbsentRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetAbsentRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.Absent,
    getAbsentRankingApi
  );

  const getAmendedRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetAmendedRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.Amended,
    getAmendedRankingApi
  );

  const getOnLeaveRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetOnLeaveRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.OnLeave,
    getOnLeaveRankingApi
  );

  const getUndertimeRankingApi = (response: GetLateRankingResponse) => {
    setResponse(response);
  };
  useQyGetUndertimeRanking(
    startDate,
    endDate,
    branchId,
    type === DashboardRanking.Undertime,
    getUndertimeRankingApi
  );

  // LOCAL FUNCTIONS
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };

  // RENDER
  const tallyTemplate = (cell: EmployeePerformanceModel) => {
    const istallyDays = tallyDays.includes(type as DashboardRanking);

    if (istallyDays) {
      return <div>{cell.tally} day(s)</div>;
    }

    const itemTally = cell.tally ?? 0;
    const tallyValue =
      itemTally < 60
        ? `${itemTally} ms`
        : `${numberFormat(itemTally / 60, 1)} hr`;
    return <div>{tallyValue}</div>;
  };

  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{response?.count}</b>
      </h5>

      <DataTable
        value={response?.data}
        selectionMode="single"
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column
          body={(cell) => (
            <div className="flex flex-col">
              <span className="font-bold">
                {cell.person_last_name}, {cell.person_first_name}
                {cell.person_middle_name}
              </span>
              {cell.employee_no ? (
                <span className="text-sm text-gray-500">
                  {cell.employee_no}
                </span>
              ) : null}
            </div>
          )}
          header="Employee"
          sortable
        ></Column>
        <Column header="Branch" field="branch_name" sortable></Column>
        <Column header="Department" field="department_name" sortable></Column>
        <Column
          header="Tally"
          body={(cell) => tallyTemplate(cell)}
          sortable
        ></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={response?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <Sidebar
      visible={true}
      onHide={() => navigate("../")}
      className="w-[800px]"
    >
      <div id="RankingDetail" className="late-ranking">
        <h2>{type} Ranking</h2>
        {grid}
      </div>
    </Sidebar>
  );
}

export default RankingDetail;
